import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";

import { uploadFile } from "~/libs/uploadFile";

import FileField from "./FileField";
import { TFileFieldFile, TFileFieldProps } from "./types";

export type TFileFieldControllerProps = Omit<
  TFileFieldProps,
  "value" | "onUpload" | "onDelete" | "loading"
> & {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  initialFile?: TFileFieldFile | null;
  rules?: RegisterOptions;
  onUploadError?: (message: string) => void;
  onUploadSuccess?: (file: TFileFieldFile) => void;
  uploadFn?: (file: File) => Promise<TFileFieldFile | undefined>;
};

const toFileValue = (
  fileId: string | null | undefined,
  initialFile?: TFileFieldFile | null,
): TFileFieldFile | null => {
  if (!fileId && !initialFile?.id) return null;

  return {
    id: fileId || initialFile?.id || "",
    name: initialFile?.name || "file",
    link: initialFile?.link || "",
  };
};

export const FileFieldController = ({
  name,
  control,
  initialFile,
  rules,
  onUploadError,
  onUploadSuccess,
  ...fieldProps
}: TFileFieldControllerProps) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ value, onChange }) => (
      <FileFieldControllerInner
        value={value}
        onChange={onChange}
        initialFile={initialFile}
        onUploadError={onUploadError}
        onUploadSuccess={onUploadSuccess}
        {...fieldProps}
      />
    )}
  />
);

type TInnerProps = Omit<TFileFieldControllerProps, "name" | "control"> & {
  value: string | null;
  onChange: (value: string | null) => void;
};

const FileFieldControllerInner = ({
  value,
  onChange,
  initialFile,
  onUploadError,
  onUploadSuccess,
  uploadFn,
  maxSizeMb = 5,
  ...fieldProps
}: TInnerProps) => {
  const [loading, setLoading] = useState(false);
  const [fileMeta, setFileMeta] = useState<TFileFieldFile | null>(
    initialFile ?? null,
  );
  const appliedInitialFileId = useRef(initialFile?.id);

  useEffect(() => {
    if (initialFile?.id && initialFile.id !== appliedInitialFileId.current) {
      appliedInitialFileId.current = initialFile.id;
      setFileMeta(initialFile);
    }
  }, [initialFile]);

  const currentFile = useMemo(
    () => toFileValue(value, fileMeta),
    [value, fileMeta],
  );

  const handleUpload = useCallback(
    async (file: File) => {
      setLoading(true);
      try {
        const uploaded = uploadFn
          ? await uploadFn(file)
          : await uploadFile(file, () => {
              onUploadError?.("Ошибка загрузки файла");
            });

        if (!uploaded?.id) return;

        const nextFile: TFileFieldFile = {
          id: uploaded.id,
          name: uploaded.name || file.name,
          link: uploaded.link,
        };

        setFileMeta(nextFile);
        onChange(uploaded.id);
        onUploadSuccess?.(nextFile);
      } catch {
        onUploadError?.("Ошибка загрузки файла");
      } finally {
        setLoading(false);
      }
    },
    [onChange, onUploadError, onUploadSuccess, uploadFn],
  );

  const handleDelete = useCallback(() => {
    setFileMeta(null);
    onChange(null);
  }, [onChange]);

  return (
    <FileField
      {...fieldProps}
      value={currentFile}
      loading={loading}
      maxSizeMb={maxSizeMb}
      onUpload={handleUpload}
      onDelete={handleDelete}
    />
  );
};

export default FileFieldController;
